import {
  posthogEvents,
  relewiseRecommendations,
  relewiseTags
} from '@/shared/constants/constants';
import { initHobbiiStarter } from '@/shared/instances/hobbii-starter';
import { onRelewiseRecommendationRegistry } from '@/shared/instances/relewise-registry-hook';
import { isFeatureFlagEnabled } from '@/shared/pinia/modules/posthog/feature-flags';
import { posthogLog } from '@/shared/pinia/modules/posthog/logger';
import { usePostHogStore } from '@/shared/pinia/modules/posthog/posthog.store';
import { trackSection } from '@/shared/tracking/section-tracking';
import { notifyBugsnagWithContext } from '@/shared/utils/error';
import { domContentLoaded, settleWithin } from '@/shared/utils/promise';

export type RecommendationType = keyof typeof relewiseRecommendations.TAGS;
export type RecommendationVariant =
  (typeof relewiseRecommendations.VARIANTS)[keyof typeof relewiseRecommendations.VARIANTS];
export type RecommendationSectionType =
  (typeof relewiseRecommendations.SECTION_TYPES)[keyof typeof relewiseRecommendations.SECTION_TYPES];
export type RecommendationSource =
  (typeof relewiseRecommendations.SOURCES)[keyof typeof relewiseRecommendations.SOURCES];
export type RecommendationProductType =
  (typeof relewiseRecommendations.PRODUCT_TYPES)[keyof typeof relewiseRecommendations.PRODUCT_TYPES];

type RecommendationStateDetail = {
  loading: boolean;
  hasResults: boolean;
};

type CreateRecommendationElementOptions = {
  type: RecommendationType;
  location: string;
  count: number;
  target?: string;
  productId?: string;
  scrollRow?: boolean;
};

type AttachRowArrowsOptions = {
  container: HTMLElement;
  widget: HTMLElement;
  template: HTMLTemplateElement | null;
};

type SlotViewedPayload = {
  flagKey: string;
  variant: RecommendationVariant;
  location: string;
  source: RecommendationSource;
  productType?: RecommendationProductType;
};

// The widget host's own :host grid rules lose to these outer classes, which
// lay the tiles out as one scrolling row matching the Shopify slider
// (2.2 tiles on mobile, 3 at md, 4 at lg, 1rem gap).
// The scrollbar is hidden and a swipe stops on a tile edge like Swiper; the hb-
// class carries the tile snap-align rule (see tailwind.css). Proximity rather
// than mandatory snapping, because the row's true end is not a tile-start snap
// point and mandatory snapping would stop short of it, leaving a gap.
const SCROLL_ROW_CLASSES =
  'hb-relewise-row grid grid-flow-col grid-cols-none auto-cols-[calc((100%-1rem)/2.2)] md:auto-cols-[calc((100%-2rem)/3)] lg:auto-cols-[calc((100%-3rem)/4)] gap-4 overflow-x-auto snap-x snap-proximity [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

const TILE_IMAGE_SELECTOR = 'figure, img';

const ARROWS = {
  PREV: '.slider-prev',
  NEXT: '.slider-next',
  DISABLED_CLASS: 'swiper-button-disabled',
  TOP_PROPERTY: '--arrow-top',
  CONTAINER_CLASS: 'relative',
  EDGE_TOLERANCE_PX: 1
} as const;

const arrowsAttached = new WeakSet<HTMLElement>();

const ATTRIBUTES = {
  LOCATION: 'displayed-at-location',
  COUNT: 'number-of-recommendations',
  TARGET: 'target',
  PRODUCT_ID: 'product-id'
} as const;

export const isRecommendationType = (
  value: string | undefined
): value is RecommendationType =>
  value !== undefined &&
  Object.prototype.hasOwnProperty.call(relewiseRecommendations.TAGS, value);

export const resolveRecommendationVariant = async (
  flagKey: string
): Promise<RecommendationVariant | null> => {
  try {
    const flagValue = await settleWithin(
      (async () => {
        await initHobbiiStarter();
        return usePostHogStore().cacheFeatureFlag(flagKey);
      })(),
      relewiseRecommendations.READY_TIMEOUT_MS
    );
    if (flagValue === null || flagValue === undefined) return null;
    // The PostHog store turns 'control' into false, so a disabled flag or a visitor
    // outside the rollout also lands here as control: run experiments at 100% rollout.
    return isFeatureFlagEnabled(flagValue)
      ? relewiseRecommendations.VARIANTS.TEST
      : relewiseRecommendations.VARIANTS.CONTROL;
  } catch (error) {
    notifyBugsnagWithContext('resolveRecommendationVariant', error);
    return null;
  }
};

const isRecommendationRegistryReady = (): boolean =>
  window[relewiseRecommendations.REGISTRY_WINDOW_KEY] !== undefined &&
  typeof window.RelewiseWebComponents?.registerRecommendationTarget ===
    'function';

export const waitForRelewise = async (): Promise<boolean> => {
  // The app builds its registry at DOMContentLoaded, which on a slow phone can
  // be long after this module runs, so the timeout starts from there.
  await domContentLoaded();
  const ready = await settleWithin(
    new Promise<boolean>((resolve) => {
      onRelewiseRecommendationRegistry(() => {
        if (isRecommendationRegistryReady()) resolve(true);
      });
    }),
    relewiseRecommendations.READY_TIMEOUT_MS
  );
  return ready ?? false;
};

export const excludeProductFromTarget = (
  target: string,
  productId: string
): void => {
  window.RelewiseWebComponents?.registerRecommendationTarget?.(target, {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    filters: (builder: any) => builder.addProductIdFilter(productId, true)
  });
};

export const createRecommendationElement = ({
  type,
  location,
  count,
  target,
  productId,
  scrollRow
}: CreateRecommendationElementOptions): HTMLElement => {
  const element = document.createElement(relewiseRecommendations.TAGS[type]);
  element.setAttribute(ATTRIBUTES.LOCATION, location);
  element.setAttribute(ATTRIBUTES.COUNT, String(count));
  // An unregistered target logs a console error and is ignored by the widget,
  // so only set it when the caller has registered a filter for it.
  if (target) element.setAttribute(ATTRIBUTES.TARGET, target);
  if (productId) element.setAttribute(ATTRIBUTES.PRODUCT_ID, productId);
  if (scrollRow) element.className = SCROLL_ROW_CLASSES;
  return element;
};

const getTiles = (widget: HTMLElement): HTMLElement[] =>
  Array.from(
    widget.shadowRoot?.querySelectorAll<HTMLElement>(
      relewiseTags.PRODUCT_TILE_TAG
    ) ?? []
  );

const hasRenderedCard = (widget: HTMLElement): boolean =>
  getTiles(widget).some(
    (tile) => !!tile.shadowRoot?.querySelector(TILE_IMAGE_SELECTOR)
  );

// `hasResults` arrives before each tile renders its card into the tile's own
// shadow root. Revealing on it would swap the skeleton for an empty row that
// grows a moment later, shifting everything below it.
const onFirstCardRendered = (
  widget: HTMLElement,
  callback: () => void
): void => {
  const observedRoots = new WeakSet<ShadowRoot>();

  const finish = () => {
    observer.disconnect();
    clearTimeout(timer);
    callback();
  };

  const check = () => {
    if (hasRenderedCard(widget)) {
      finish();
      return;
    }
    [widget.shadowRoot, ...getTiles(widget).map((tile) => tile.shadowRoot)]
      .filter((root): root is ShadowRoot => !!root && !observedRoots.has(root))
      .forEach((root) => {
        observedRoots.add(root);
        observer.observe(root, { childList: true, subtree: true });
      });
  };

  const observer = new MutationObserver(check);
  const timer = setTimeout(
    finish,
    relewiseRecommendations.CARD_RENDER_TIMEOUT_MS
  );
  check();
};

export const onRecommendationsSettled = (
  element: HTMLElement,
  callback: (hasResults: boolean) => void
): void => {
  const stopListening = () => {
    element.removeEventListener(
      relewiseRecommendations.STATE_CHANGED_EVENT,
      onStateChanged
    );
    clearTimeout(timer);
  };

  const onStateChanged = (event: Event) => {
    if (!(event instanceof CustomEvent)) return;
    const detail: RecommendationStateDetail = event.detail;
    if (detail.loading) return;
    stopListening();
    if (detail.hasResults) {
      onFirstCardRendered(element, () => callback(true));
    } else {
      callback(false);
    }
  };

  element.addEventListener(
    relewiseRecommendations.STATE_CHANGED_EVENT,
    onStateChanged
  );
  const timer = setTimeout(() => {
    stopListening();
    callback(false);
  }, relewiseRecommendations.RESULTS_TIMEOUT_MS);
};

export const onSlotViewed = (
  element: HTMLElement,
  callback: () => void
): void => {
  const observer = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    observer.disconnect();
    callback();
  });
  observer.observe(element);
};

export const trackSlotViewed = ({
  flagKey,
  variant,
  location,
  source,
  productType
}: SlotViewedPayload): void => {
  const data = {
    flag_key: flagKey,
    variant,
    location,
    source,
    ...(productType && { product_type: productType })
  };
  posthogLog(posthogEvents.RECOMMENDATION_SLOT_VIEWED, data);
  window.posthog?.capture(posthogEvents.RECOMMENDATION_SLOT_VIEWED, data);
};

export const createSourceDecision = (): {
  decide: (source: RecommendationSource) => void;
  decided: Promise<RecommendationSource>;
} => {
  let decide: (source: RecommendationSource) => void = () => {};
  const decided = new Promise<RecommendationSource>((resolve) => {
    decide = resolve;
  });
  return { decide, decided };
};

export const startSlotTracking = (
  element: HTMLElement,
  sectionId: string,
  type: RecommendationSectionType
): void => {
  element.dataset.trackSection = '';
  element.dataset.trackSectionId = sectionId;
  element.dataset.trackSectionType = type;
  trackSection(element);
};

const setArrowState = (button: HTMLButtonElement, disabled: boolean) => {
  button.disabled = disabled;
  button.classList.toggle(ARROWS.DISABLED_CLASS, disabled);
  button.setAttribute('aria-disabled', String(disabled));
};

export const attachRowArrows = ({
  container,
  widget,
  template
}: AttachRowArrowsOptions): void => {
  if (!template || arrowsAttached.has(widget)) return;
  arrowsAttached.add(widget);

  container.classList.add(ARROWS.CONTAINER_CLASS);
  container.appendChild(template.content.cloneNode(true));

  const prev = container.querySelector<HTMLButtonElement>(ARROWS.PREV);
  const next = container.querySelector<HTMLButtonElement>(ARROWS.NEXT);
  if (!prev || !next) return;

  const getStep = (): number => {
    const [tile] = getTiles(widget);
    if (!tile) return 0;
    const gap = parseFloat(getComputedStyle(widget).columnGap) || 0;
    return tile.getBoundingClientRect().width + gap;
  };

  const scrollByTile = (direction: number) => {
    widget.scrollBy({ left: direction * getStep(), behavior: 'smooth' });
  };

  const updateArrowTop = () => {
    const [tile] = getTiles(widget);
    const imageHeight = tile?.shadowRoot
      ?.querySelector<HTMLElement>(TILE_IMAGE_SELECTOR)
      ?.getBoundingClientRect().height;
    if (!imageHeight) return;
    container.style.setProperty(
      ARROWS.TOP_PROPERTY,
      `${imageHeight / 2 - prev.offsetHeight / 2}px`
    );
  };

  const updateState = () => {
    const maxScroll = widget.scrollWidth - widget.clientWidth;
    setArrowState(prev, widget.scrollLeft <= ARROWS.EDGE_TOLERANCE_PX);
    setArrowState(
      next,
      widget.scrollLeft >= maxScroll - ARROWS.EDGE_TOLERANCE_PX
    );
  };

  const refresh = () => {
    if (!widget.isConnected) {
      teardown();
      return;
    }
    updateArrowTop();
    updateState();
  };

  // Scrolling can't change the image height, so only the cheap edge check runs here.
  const onScroll = () => {
    if (!widget.isConnected) {
      teardown();
      return;
    }
    updateState();
  };

  const resizeObserver = new ResizeObserver(refresh);
  const observedRoots = new WeakSet<ShadowRoot>();
  const observedTiles = new WeakSet<HTMLElement>();
  const observedImages = new WeakSet<HTMLElement>();
  // A tile's card renders in the tile's own shadow root, which neither the
  // tile's outer size nor the widget's shadow root mutations reveal.
  const observeTiles = () => {
    getTiles(widget).forEach((tile) => {
      if (!observedTiles.has(tile)) {
        observedTiles.add(tile);
        resizeObserver.observe(tile);
      }
      const tileRoot = tile.shadowRoot;
      if (tileRoot && !observedRoots.has(tileRoot)) {
        observedRoots.add(tileRoot);
        mutationObserver.observe(tileRoot, { childList: true, subtree: true });
      }
      const image = tileRoot?.querySelector<HTMLElement>(TILE_IMAGE_SELECTOR);
      if (image && !observedImages.has(image)) {
        observedImages.add(image);
        resizeObserver.observe(image);
      }
    });
  };

  const mutationObserver = new MutationObserver(() => {
    observeTiles();
    refresh();
  });

  const teardown = () => {
    resizeObserver.disconnect();
    mutationObserver.disconnect();
    widget.removeEventListener('scroll', onScroll);
  };

  prev.addEventListener('click', () => scrollByTile(-1));
  next.addEventListener('click', () => scrollByTile(1));
  widget.addEventListener('scroll', onScroll, { passive: true });
  resizeObserver.observe(widget);
  if (widget.shadowRoot) {
    mutationObserver.observe(widget.shadowRoot, {
      childList: true,
      subtree: true
    });
  }
  observeTiles();
  refresh();
};
